// src/pages/Connector/ServiceNow/TicketAnalysisModal.js
// Redesigned with Material UI styling

import React, { useState, useEffect } from "react";
import {
  Modal,
  Box,
  Button,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Typography,
  Chip,
  Divider,
  Paper,
  useTheme,
} from "@mui/material";
import axiosInstance from "../../../../utils/axiosInstance";

export default function TicketAnalysisModal({ isOpen, onClose, ticket, permission }) {
  const theme = useTheme();
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [training, setTraining] = useState(false);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    if (isOpen && ticket.is_analyzed) {
      fetchAnalysis();
    }
  }, [isOpen, ticket]);

  const fetchAnalysis = async () => {
    setLoading(true);
    setError("");

    try {
      const response = await axiosInstance.get(
        `/api/servicenow/tickets/${ticket.id}/analysis`
      );
      if (response.data.success) {
        setAnalysis(response.data.data.analysis);
      }
    } catch (err) {
      console.error("Error fetching analysis:", err);
      setError(err.response?.data?.message || "Failed to fetch analysis");
    } finally {
      setLoading(false);
    }
  };

  const handleAnalyze = async () => {
    setAnalyzing(true);
    setError("");

    try {
      const response = await axiosInstance.post(
        `/api/servicenow/tickets/${ticket.id}/analyze`
      );
      if (response.data.success) {
        // ✅ NEW: Fetch the complete analysis to ensure all fields are populated
        await fetchAnalysis();
        setActiveTab(0); // ✅ NEW: Reset to Overview tab
        setError("Ticket analyzed successfully!");
      } else {
        setError(response.data.message || "Analysis failed");
      }
    } catch (err) {
      console.error("Error analyzing ticket:", err);
      setError(err.response?.data?.message || "Failed to analyze ticket");
    } finally {
      setAnalyzing(false);
    }
  };

  const handleTrain = async () => {
    setTraining(true);
    setError("");

    try {
      const response = await axiosInstance.post(
        `/api/servicenow/tickets/${ticket.id}/train`
      );
      if (response.data.success) {
        setError("Analysis trained successfully!");
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        setError(response.data.message || "Training failed");
      }
    } catch (err) {
      console.error("Error training analysis:", err);
      setError(err.response?.data?.message || "Failed to train analysis");
    } finally {
      setTraining(false);
    }
  };

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: { xs: "95%", sm: "90%", md: "80%" },
    maxWidth: "1000px",
    maxHeight: "90vh",
    bgcolor: "background.paper",
    boxShadow: 24,
    p: 0,
    borderRadius: 2,
    outline: "none",
    overflowY: "auto",
  };

  const renderSentimentBar = (sentiment) => {
    if (!sentiment) return null;

    const {
      positive_percentage = 0,
      negative_percentage = 0,
      neutral_percentage = 0,
    } = sentiment;

    return (
      <Box sx={{ mt: 2 }}>
        <Box
          sx={{
            display: "flex",
            height: "30px",
            borderRadius: 1,
            overflow: "hidden",
          }}
        >
          {positive_percentage > 0 && (
            <Box
              sx={{
                width: `${positive_percentage}%`,
                backgroundColor: theme.palette.success.main,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "white",
                fontSize: "0.85em",
              }}
            >
              {positive_percentage > 10 && `${positive_percentage}%`}
            </Box>
          )}
          {neutral_percentage > 0 && (
            <Box
              sx={{
                width: `${neutral_percentage}%`,
                backgroundColor: theme.palette.warning.main,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "white",
                fontSize: "0.85em",
              }}
            >
              {neutral_percentage > 10 && `${neutral_percentage}%`}
            </Box>
          )}
          {negative_percentage > 0 && (
            <Box
              sx={{
                width: `${negative_percentage}%`,
                backgroundColor: theme.palette.error.main,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "white",
                fontSize: "0.85em",
              }}
            >
              {negative_percentage > 10 && `${negative_percentage}%`}
            </Box>
          )}
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            mt: 1,
            fontSize: "0.85em",
          }}
        >
          <Typography variant="caption">
            🟢 Positive: {positive_percentage}%
          </Typography>
          <Typography variant="caption">
            🟡 Neutral: {neutral_percentage}%
          </Typography>
          <Typography variant="caption">
            🔴 Negative: {negative_percentage}%
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <Modal open={isOpen} onClose={onClose} aria-labelledby="analysis-modal-title">
      <Box sx={modalStyle}>
        {/* Modal Header */}
        <Box
          sx={{
            p: 3,
            borderBottom: `1px solid ${theme.palette.divider}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h5" color="text.primary">
            <i className="fa fa-server" style={{ marginRight: "8px" }} />
            Ticket Analysis: {ticket.ticket_number}
          </Typography>
          <Button
            onClick={onClose}
            size="small"
            sx={{ minWidth: "auto" }}
            disabled={analyzing || training}
          >
            <i className="fa fa-times" style={{ fontSize: "20px" }} />
          </Button>
        </Box>

        {/* Modal Content */}
        <Box sx={{ p: 3 }}>
          {/* Ticket Info */}
          <Paper
            sx={{
              p: 2,
              mb: 3,
              bgcolor: theme.palette.mode === "dark" ? "#1e293b" : "#f8f9fa",
            }}
          >
            {/* First row: Description + State */}
            <Box display="flex" flexWrap="wrap" justifyContent="space-between" mb={1}>
                <Typography
                variant="body2"
                color="text.primary"
                sx={{ flex: 1, mr: 2, minWidth: "250px" }}
                >
                <strong>Description:</strong> {ticket.short_description || "N/A"}
                </Typography>
                <Typography
                variant="body2"
                color="text.primary"
                sx={{ minWidth: "120px" }}
                >
                <strong>State:</strong> {ticket.state || "N/A"}
                </Typography>
            </Box>

            {/* Second row: Comments + Work Notes */}
            <Box display="flex" flexWrap="wrap" justifyContent="space-between" mb={1}>
                <Typography
                variant="body2"
                color="text.primary"
                sx={{ flex: 1, mr: 2, minWidth: "250px" }}
                >
                <strong>Comments:</strong> {ticket.comments_count || 0}
                </Typography>
                <Typography
                variant="body2"
                color="text.primary"
                sx={{ minWidth: "120px"  }}
                >
                <strong>Work Notes:</strong> {ticket.work_notes_count || 0}
                </Typography>
            </Box>

            <Box display="flex" gap={1} flexWrap="wrap" mt={1}>
              <Chip
                label={`Priority: ${ticket.priority}`}
                size="small"
                color="primary"
              />
              <Chip label={`State: ${ticket.state}`} size="small" color="info" />
              {ticket.assigned_to && (
                <Chip
                  label={`Assigned: ${ticket.assigned_to}`}
                  size="small"
                  variant="outlined"
                />
              )}
            </Box>
          </Paper>

          {/* Error Alert */}
          {error && (
            <Alert
              severity={
                error.toLowerCase().includes("success") ? "success" : "error"
              }
              sx={{ mb: 3 }}
              onClose={() => setError("")}
            >
              {error}
            </Alert>
          )}

          {/* Loading State */}
          {loading && (
            <Box display="flex" flexDirection="column" alignItems="center" py={4}>
              <CircularProgress size={40} />
              <Typography mt={2} color="text.primary">
                Loading analysis...
              </Typography>
            </Box>
          )}

          {/* Not Analyzed State */}
          {!loading && !analysis && !ticket.is_analyzed && (
            <Box textAlign="center" py={4}>
              <i
                className="fa fa-bar-chart fa-4x"
                style={{ color: theme.palette.text.disabled, marginBottom: "16px" }}
              />
              <Typography variant="h6" color="text.primary" mb={2}>
                Ticket Not Analyzed
              </Typography>
              <Typography color="text.secondary" mb={3}>
                This ticket hasn’t been analyzed yet. Click below to start the analysis. After it’s analyzed, you’ll be able to train your chatbot with it.
              </Typography>
              {permission.canModify && (
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleAnalyze}
                  disabled={analyzing}
                  startIcon={
                    analyzing ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <i className="fa fa-bar-chart" />
                    )
                  }
                >
                  {analyzing ? "Analyzing..." : "Analyze Ticket"}
                </Button>
              )}
            </Box>
          )}

          {/* Analysis Results */}
          {!loading && analysis && (
            <Box>
              {/* Tabs */}
              <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
                <Tabs
                  value={activeTab}
                  onChange={(e, newValue) => setActiveTab(newValue)}
                  variant="scrollable"
                  scrollButtons="auto"
                  textColor="primary"
                  indicatorColor="primary"
                >
                  <Tab label="Overview" />
                  <Tab label="Key Entries" />
                  <Tab label="Resolution" />
                  <Tab label="Sentiment" />
                  <Tab label="Tags" />
                  <Tab label="Training Content" />
                </Tabs>
              </Box>

              {/* Tab Content */}
              <Box sx={{ padding: 2}}>
                {/* Overview Tab */}
                {activeTab === 0 && (
                  <Box>
                    {analysis.summary && (
                      <Box mb={3}>
                        <Typography variant="h6" mb={1}>
                          Summary
                        </Typography>
                        <Paper
                          sx={{
                            p: 2,
                            bgcolor:
                              theme.palette.mode === "dark" ? "#1e293b" : "#f8f9fa",
                          }}
                        >
                          <Typography
                            variant="body2"
                            color="text.primary"
                            sx={{ whiteSpace: "pre-wrap" }}
                          >
                            {analysis.summary}
                          </Typography>
                        </Paper>
                      </Box>
                    )}

                    <Box display="flex" gap={2} flexWrap="wrap">
                      {analysis.urgency_score && (
                        <Paper
                          sx={{
                            p: 2,
                            flex: 1,
                            minWidth: "200px",
                            textAlign: "center",
                          }}
                        >
                          <Typography variant="caption" color="text.secondary">
                            Urgency Score
                          </Typography>
                          <Typography variant="h4" color="primary">
                            {analysis.urgency_score}/10
                          </Typography>
                        </Paper>
                      )}
                      {analysis.complexity_score && (
                        <Paper
                          sx={{
                            p: 2,
                            flex: 1,
                            minWidth: "200px",
                            textAlign: "center",
                          }}
                        >
                          <Typography variant="caption" color="text.secondary">
                            Complexity Score
                          </Typography>
                          <Typography variant="h4" color="warning.main">
                            {analysis.complexity_score}/10
                          </Typography>
                        </Paper>
                      )}
                      {analysis.overall_quality && (
                        <Paper
                          sx={{
                            p: 2,
                            flex: 1,
                            minWidth: "200px",
                            textAlign: "center",
                          }}
                        >
                          <Typography variant="caption" color="text.secondary">
                            Overall Quality
                          </Typography>
                          <Typography variant="h4" color="success.main">
                            {analysis.overall_quality}/10
                          </Typography>
                        </Paper>
                      )}
                    </Box>
                  </Box>
                )}

                {/* Key Entries Tab */}
                {activeTab === 1 && (
                  <Box sx={{ padding: 2}}>
                    <Typography variant="h6" mb={2}>
                      Key Entries
                    </Typography>
                    {analysis.key_entries && analysis.key_entries.length > 0 ? (
                      <Box display="flex" flexDirection="column" gap={2}>
                        {analysis.key_entries.map((entry, index) => (
                          <Paper
                            key={index}
                            sx={{
                              p: 2,
                              bgcolor:
                                theme.palette.mode === "dark"
                                  ? "#1e293b"
                                  : "#f8f9fa",
                            }}
                          >
                            <Box
                              display="flex"
                              justifyContent="space-between"
                              alignItems="center"
                              mb={1}
                            >
                              <Typography variant="subtitle2" fontWeight="bold">
                                Entry {index + 1}
                              </Typography>
                              <Box display="flex" gap={1}>
                                <Chip
                                  label={entry.entry_type || "N/A"}
                                  size="small"
                                  color="primary"
                                />
                                <Chip
                                  label={`Relevance: ${entry.relevance_score}/10`}
                                  size="small"
                                  variant="outlined"
                                />
                              </Box>
                            </Box>
                            <Typography
                              variant="body2"
                              color="text.primary"
                              sx={{ whiteSpace: "pre-wrap" }}
                            >
                              {entry.text}
                            </Typography>
                          </Paper>
                        ))}
                      </Box>
                    ) : (
                      <Typography color="text.secondary">
                        No key entries available
                      </Typography>
                    )}
                  </Box>
                )}

                {/* Resolution Tab */}
                {activeTab === 2 && (
                  <Box sx={{ padding: 2}}>
                    {analysis.root_cause && (
                      <Box mb={3}>
                        <Typography variant="h6" mb={1}>
                          Root Cause
                        </Typography>
                        <Paper
                          sx={{
                            p: 2,
                            bgcolor:
                              theme.palette.mode === "dark" ? "#1e293b" : "#f8f9fa",
                          }}
                        >
                          <Typography
                            variant="body2"
                            color="text.primary"
                            sx={{ whiteSpace: "pre-wrap" }}
                          >
                            {analysis.root_cause}
                          </Typography>
                        </Paper>
                      </Box>
                    )}

                    {analysis.solution && (
                      <Box mb={3}>
                        <Typography variant="h6" mb={1}>
                          Solution
                        </Typography>
                        <Paper
                          sx={{
                            p: 2,
                            bgcolor:
                              theme.palette.mode === "dark" ? "#1e293b" : "#f8f9fa",
                          }}
                        >
                          <Typography
                            variant="body2"
                            color="text.primary"
                            sx={{ whiteSpace: "pre-wrap" }}
                          >
                            {analysis.solution}
                          </Typography>
                        </Paper>
                      </Box>
                    )}

                    {analysis.resolution_steps &&
                      analysis.resolution_steps.length > 0 && (
                        <Box mb={3}>
                          <Typography variant="h6" mb={1}>
                            Resolution Steps
                          </Typography>
                          <Paper
                            sx={{
                              p: 2,
                              bgcolor:
                                theme.palette.mode === "dark"
                                  ? "#1e293b"
                                  : "#f8f9fa",
                            }}
                          >
                            <Box component="ol" sx={{ pl: 2 }}>
                              {analysis.resolution_steps.map((step, index) => (
                                <Typography
                                  key={index}
                                  component="li"
                                  variant="body2"
                                  color="text.primary"
                                  sx={{ mb: 1 }}
                                >
                                  {step}
                                </Typography>
                              ))}
                            </Box>
                          </Paper>
                        </Box>
                      )}

                    {!analysis.root_cause &&
                      !analysis.solution &&
                      !analysis.resolution_steps && (
                        <Typography color="text.secondary">
                          No resolution information available
                        </Typography>
                      )}
                  </Box>
                )}

                {/* Sentiment Tab */}
                {activeTab === 3 && (
                  <Box sx={{ padding: 2}}>
                    <Typography variant="h6" mb={2}>
                      Sentiment Analysis
                    </Typography>
                    {analysis.sentiment && (
                      <Box>
                        <Box mb={3}>
                          <Typography variant="body2" mb={1}>
                            <strong>Overall Sentiment:</strong>
                          </Typography>
                          <Chip
                            label={analysis.sentiment.overall}
                            color={
                              analysis.sentiment.overall === "positive"
                                ? "success"
                                : analysis.sentiment.overall === "negative"
                                ? "error"
                                : analysis.sentiment.overall === "mixed"
                                ? "warning"
                                : "default"
                            }
                            sx={{ textTransform: "capitalize" }}
                          />
                        </Box>

                        {renderSentimentBar(analysis.sentiment)}

                        <Paper
                          sx={{
                            p: 2,
                            mt: 3,
                            bgcolor:
                              theme.palette.mode === "dark" ? "#1e293b" : "#f8f9fa",
                          }}
                        >
                          <Typography variant="body2" color="text.primary">
                            <strong>Engagement Score:</strong>{" "}
                            {analysis.sentiment.engagement_score}/10
                          </Typography>
                        </Paper>
                      </Box>
                    )}
                  </Box>
                )}

                {/* Tags Tab */}
                {activeTab === 4 && (
                  <Box sx={{ padding: 2}}>
                    <Typography variant="h6" mb={2}>
                      Classification Tags
                    </Typography>
                    {analysis.tags && analysis.tags.length > 0 ? (
                      <Paper
                        sx={{
                          p: 2,
                          bgcolor:
                            theme.palette.mode === "dark" ? "#1e293b" : "#f8f9fa",
                        }}
                      >
                        <Box display="flex" gap={1} flexWrap="wrap">
                          {analysis.tags.map((tag, index) => (
                            <Chip
                              key={index}
                              label={tag}
                              color="primary"
                              size="medium"
                            />
                          ))}
                        </Box>
                      </Paper>
                    ) : (
                      <Typography color="text.secondary">
                        No tags available
                      </Typography>
                    )}
                  </Box>
                )}

                {/* Training Content Tab */}
                {activeTab === 5 && (
                  <Box sx={{ padding: 2}}>
                    <Typography variant="h6" mb={2}>
                      Merged Training Content
                    </Typography>
                    <Paper
                      sx={{
                        p: 2,
                        bgcolor:
                          theme.palette.mode === "dark" ? "#1e293b" : "#f8f9fa",
                        maxHeight: "400px",
                        overflowY: "auto",
                      }}
                    >
                      <Typography
                        variant="body2"
                        color="text.primary"
                        sx={{
                          whiteSpace: "pre-wrap",
                          fontFamily: "monospace",
                        }}
                      >
                        {analysis.merged_content}
                      </Typography>
                    </Paper>
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </Box>

        {/* Modal Footer */}
        <Box
          sx={{
            p: 3,
            borderTop: `1px solid ${theme.palette.divider}`,
            display: "flex",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Button
            variant="outlined"
            onClick={onClose}
            disabled={analyzing || training}
          >
            Close
          </Button>
          <Box display="flex" gap={1}>
            {permission.canModify && analysis && (
              <Button
                variant="outlined"
                onClick={handleAnalyze}
                disabled={analyzing || training}
                startIcon={<i className="fa fa-refresh" />}
              >
                Re-analyze
              </Button>
            )}
            {permission.canModify && analysis && !ticket.is_trained && (
              <Button
                variant="contained"
                onClick={handleTrain}
                disabled={analyzing || training}
                startIcon={
                  training ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <i className="fa fa-graduation-cap" />
                  )
                }
              >
                {training ? "Training..." : "Train Chatbot"}
              </Button>
            )}
            {ticket.is_trained && (
              <Chip
                label="Already Trained"
                color="success"
                icon={<i className="fa fa-check" />}
                sx={{ px: 2 }}
              />
            )}
          </Box>
        </Box>
      </Box>
    </Modal>
  );
}